import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { IndianRupee, TrendingUp, TrendingDown, PieChart as PieIcon } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#0d9488', '#f59e0b', '#6366f1', '#ec4899', '#10b981', '#8b5cf6', '#ef4444'];

const AccountingReports = () => {
  const [report, setReport] = useState(null);
  const [vendorRollup, setVendorRollup] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        // Parallel fetch — accounting summary + per-vendor rollup so the
        // "Top vendors by spend" card can render alongside the P&L. The
        // rollup endpoint already returns vendorName + totalFinal per
        // supplier (Phase 16.1 fix), so no extra backend work needed.
        const [s, v] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/accounting/reports/summary`),
          axios.get(`${BACKEND_URL}/api/vendors/rollup`),
        ]);
        setReport(s.data);
        setVendorRollup(v.data?.items || []);
      } catch (err) {
        // toast.error not available here — keep silent fallback.
      } finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div></div>;
  if (!report) return <p className="text-center text-slate-500 py-16">Failed to load report data</p>;

  const { totalRevenue, totalExpenses, profit, gstCollected, tcsCollected, byCategory, byPaymentStatus, totalFees, gatewayFeesTotal, remittanceFeesTotal, vendorPaymentMethodFeesTotal } = report;
  // Revenue excludes GST per business request — "Don't show Taxes part in Revenue".
  // The full GST/TCS breakdown lives in the dedicated Taxes tab.
  const netRevenueExGst = (totalRevenue || 0) - (gstCollected || 0) - (tcsCollected || 0);

  const categoryData = Object.entries(byCategory || {}).map(([name, data]) => ({ name: name || 'Unknown', value: data.total, count: data.count }));
  const statusData = Object.entries(byPaymentStatus || {}).map(([name, data]) => ({ name: name || 'Unknown', value: data.total, count: data.count }));

  // P&L breakdown — surface the largest expense categories so the agent can
  // see where margin is leaking without leaving the page. Falls back to a
  // single "Total expenses" line when no categories are recorded yet.
  // Reconciliation hookup (2026-09) — payment gateway / FX remittance /
  // vendor card fees are real costs that never lived in `totalExpenses`
  // (they're embedded fields, deliberately not a second parallel expense
  // record) — subtracted here as their own explicit line so profit is
  // never silently overstated.
  const netProfit = netRevenueExGst - (totalExpenses || 0) - (totalFees || 0);
  const margin = netRevenueExGst > 0 ? (netProfit / netRevenueExGst) * 100 : 0;
  const monthLabel = new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-6" data-testid="reports-tab">
      {/* Key Financial Metrics — TAX-EXCLUSIVE revenue. GST/TCS now live in the Taxes tab. */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <MetricCard label="Net Revenue (ex GST)" value={netRevenueExGst} icon={TrendingUp} color="emerald" />
        <MetricCard label="Total Expenses" value={totalExpenses} icon={TrendingDown} color="red" />
        <MetricCard label="Net Profit" value={profit} icon={IndianRupee} color={profit >= 0 ? 'emerald' : 'red'} />
      </div>

      {/* P&L STATEMENT — accounting-style line items per business request */}
      <Card className="p-6 bg-white rounded-2xl shadow-sm" data-testid="pnl-statement">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900">P&amp;L Statement — {monthLabel}</h3>
          <span className="text-xs text-slate-500">Confirmed trips only · ex tax</span>
        </div>
        <div className="space-y-1">
          <PnlRow label="Total revenue (booked)" value={totalRevenue} bold />
          <PnlRow label="− GST collected (passthrough)" value={-(gstCollected || 0)} muted />
          <PnlRow label="− TCS collected (passthrough)" value={-(tcsCollected || 0)} muted />
          <PnlRow label="Net revenue" value={netRevenueExGst} bold divider />
          {categoryData.length > 0 ? (
            categoryData
              .sort((a, b) => b.value - a.value)
              .map((c) => (
                <PnlRow
                  key={c.name}
                  label={`— ${c.name}`}
                  value={-c.value}
                  muted
                  testId={`pnl-cat-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                />
              ))
          ) : (
            <PnlRow label="— No expenses recorded" value={0} muted />
          )}
          <PnlRow label="Total expenses" value={-(totalExpenses || 0)} bold divider />
          {totalFees > 0 && (
            <PnlRow
              label="— Payment gateway / FX / vendor-card fees"
              value={-totalFees}
              muted
              testId="pnl-fees-total"
              extra={`(Gateway ₹${Math.round(gatewayFeesTotal || 0).toLocaleString('en-IN')} · Remittance ₹${Math.round(remittanceFeesTotal || 0).toLocaleString('en-IN')} · Vendor card ₹${Math.round(vendorPaymentMethodFeesTotal || 0).toLocaleString('en-IN')})`}
            />
          )}
          <PnlRow
            label="Net profit"
            value={netProfit}
            bold
            tone={netProfit >= 0 ? 'success' : 'danger'}
            extra={netRevenueExGst > 0 ? `(${margin.toFixed(1)}% margin)` : ''}
          />
        </div>
      </Card>

      {/* TOP VENDORS BY SPEND — Phase 17 micro-add. The earlier P&L only
          aggregated by category; this surface gives the operator the
          actual vendor names so they can immediately see "Hotel Le Grand
          is 28% of expenses" without leaving the page. Wired to the same
          /vendors page row so click-through reaches the vendor ledger. */}
      {vendorRollup.length > 0 && (
        <Card className="p-5 bg-white rounded-2xl shadow-sm" data-testid="top-vendors-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-900">Top vendors by spend</h3>
            <Link to="/vendors" className="text-xs text-teal-700 hover:underline">View all vendors →</Link>
          </div>
          <table className="w-full text-xs" data-testid="top-vendors-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200">
                <th className="py-2">#</th>
                <th className="py-2">Vendor name</th>
                <th className="py-2">Type</th>
                <th className="py-2 text-right">Spend</th>
                <th className="py-2 text-right">Trips</th>
                <th className="py-2 text-right">Share</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {[...vendorRollup]
                .sort((a, b) => (b.totalFinal || 0) - (a.totalFinal || 0))
                .slice(0, 8)
                .map((v, idx) => {
                  const totalSpend = vendorRollup.reduce((s, x) => s + (x.totalFinal || 0), 0);
                  const share = totalSpend > 0 ? ((v.totalFinal || 0) / totalSpend) * 100 : 0;
                  return (
                    <tr key={v.supplierRef || v.vendorId || idx} className="hover:bg-slate-50" data-testid={`top-vendor-row-${idx}`}>
                      <td className="py-1.5 text-slate-500 font-mono">{idx + 1}</td>
                      <td className="py-1.5 font-medium text-slate-800">
                        <Link
                          to={`/ledger?party_type=vendor&party_id=${encodeURIComponent(v.vendorId || v.supplierRef)}`}
                          className="hover:text-teal-700 hover:underline"
                          data-testid={`top-vendor-link-${idx}`}
                        >
                          {v.vendorName || v.supplierRef || '—'}
                        </Link>
                      </td>
                      <td className="py-1.5 text-slate-500 capitalize">{v.category || '—'}</td>
                      <td className="py-1.5 text-right font-mono font-semibold">₹{Math.round(v.totalFinal || 0).toLocaleString('en-IN')}</td>
                      <td className="py-1.5 text-right text-slate-500">{v.tripCount || 0}</td>
                      <td className="py-1.5 text-right text-slate-600 font-mono">{share.toFixed(1)}%</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </Card>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 bg-white rounded-2xl shadow-sm" data-testid="category-chart">
          <h3 className="font-semibold text-slate-900 mb-4">Expenses by Category</h3>
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={categoryData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(val) => `₹${val.toLocaleString('en-IN')}`} />
                <Bar dataKey="value" fill="#0d9488" radius={[4, 4, 0, 0]} name="Amount" />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState />}
        </Card>

        <Card className="p-6 bg-white rounded-2xl shadow-sm" data-testid="payment-status-chart">
          <h3 className="font-semibold text-slate-900 mb-4">Payment Status</h3>
          {statusData.length > 0 ? (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width="50%" height={200}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={false}>
                    {statusData.map((_, i) => <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(val) => `₹${val.toLocaleString('en-IN')}`} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-3">
                {statusData.map((s, i) => (
                  <div key={`status-${s.name}`} className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full" style={{ background: COLORS[i % COLORS.length] }}></div>
                    <span className="text-slate-600 capitalize">{s.name}: <strong>₹{s.value.toLocaleString('en-IN')}</strong> ({s.count})</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <EmptyState />}
        </Card>
      </div>
    </div>
  );
};

const RCOLOR_MAP = {
  teal: { bg: 'bg-teal-100', text: 'text-teal-600' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
  red: { bg: 'bg-red-100', text: 'text-red-600' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
  violet: { bg: 'bg-violet-100', text: 'text-violet-600' },
};

const MetricCard = ({ label, value, icon: Icon, color }) => {
  const c = RCOLOR_MAP[color] || RCOLOR_MAP.teal;
  return (
    <Card className="p-4 bg-white rounded-2xl shadow-sm" data-testid={`report-metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`w-10 h-10 ${c.bg} rounded-xl flex items-center justify-center mb-2`}>
        <Icon className={`h-5 w-5 ${c.text}`} />
      </div>
      <p className="text-xl font-bold text-slate-900">₹{(value || 0).toLocaleString('en-IN')}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </Card>
  );
};

const EmptyState = () => <p className="text-sm text-slate-400 text-center py-10">No data yet</p>;

// PnlRow — single line item inside the P&L statement card. Positive values
// render in slate, negatives (deductions) in slate-700. `bold` rows separate
// subtotals from line items, and `tone` highlights the final profit line.
const PnlRow = ({ label, value, bold = false, muted = false, divider = false, tone, extra, testId }) => {
  const toneClass = tone === 'success' ? 'text-emerald-700'
    : tone === 'danger' ? 'text-rose-700'
    : 'text-slate-900';
  return (
    <div
      data-testid={testId}
      className={`flex justify-between items-center py-1.5 ${divider ? 'border-t border-slate-200 mt-1 pt-2' : ''} ${bold ? 'font-semibold' : ''}`}
    >
      <span className={`text-sm ${muted ? 'text-slate-500' : 'text-slate-700'}`}>
        {label}
      </span>
      <span className={`text-sm font-mono ${bold ? toneClass : 'text-slate-700'}`}>
        ₹{Math.round(value || 0).toLocaleString('en-IN')}
        {extra && <span className="text-xs text-slate-500 ml-2 font-sans">{extra}</span>}
      </span>
    </div>
  );
};

export default AccountingReports;
